<template>
  <div
    class="calendar-day-item"
    :class="calendarDayItemClass"
    @mouseover="handleMouseOver"
    @mouseleave="handleMouseLeave"
  >
    <div
      class="calendar-day-item-background"
      :style="itemBackgroundStyle"
    >
      <div
        v-if="showMoreCardsCount"
        class="calendar-day-item-footer"
      >
        <v-popover
          ref="showMoreButton"
          interactive
          tag="div"
          content-tag="div"
          :trigger="'click'"
          :role="'poper'"
          :placement="'bottom-start'"
          :append-to="() => documentBody"
          class="calendar-day-item-footer-button"
          :class="{ 'show-more-active': displayShowMorePopup }"
          @state="onDropdownStateChange"
        >
          <span class="calendar-day-footer-label">{{ moreCardsButtonLabel }}</span>
          <em
            class="custom-icons calendar-day-footer-icon"
            :style="footerIconStyle"
            :class="{ 'rotate-180': displayShowMorePopup }"
          />
          <template #content="{ state, hide }">
            <CalendarCardsTooltip
              v-if="state.isVisible"
              :card-list="moreCards"
              :style="getShowMorePopupStyle()"
              @close-tippy="hide"
            />
          </template>
        </v-popover>
      </div>
    </div>
    <div
      class="calendar-day-item-header usn"
      :class="{ 'item-past': isPast }"
      :style="itemHeaderStyle"
    >
      <div class="item-date-wrap">
        <div class="item-day">{{ dayLabel }}</div>
        <div
          v-if="showMonthLabel"
          :class="monthClass"
        >
          {{ monthLabel }}
        </div>
      </div>
      <QuickCardButton
        v-if="isHovered && !inEpicBoard && !isGuestReader"
        :effect="handleQuickCardButtonClick"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import {
  format,
  isFirstDayOfMonth,
  getDate,
  isWeekend,
  startOfMonth,
  getWeekOfMonth,
  endOfDay,
  getTime,
  millisecondsToSeconds,
} from 'date-fns';
import QuickCardButton from '@/components/editor/toolbar/sections/QuickCardButton.vue';
import { browserStorageSetItem, browserStorageGetItem } from '@/utilities/browser-storage.util';
import { getQuickCardCreationDraftKey } from '@/utilities/localDrafts';
import useCustomIcon from '@/utilities/composables/useCustomIcon';
import useTranslate from '@/utilities/composables/useTranslate';
import CalendarCardsTooltip from './tooltips/CalendarCardsTooltip.vue';
import useBoardEmbed from '@/boards/composables/useBoardEmbed';
import useCurrentRole from '@/utilities/composables/useCurrentRole';
import { useStore } from 'vuex';

const props = defineProps({
  dayIndex: {
    type: Number,
    required: true,
  },
  dateTimestamp: {
    type: Number,
    required: true,
  },
  isToday: {
    type: Boolean,
    default: false,
  },
  isPast: {
    type: Boolean,
    default: false,
  },
  maxCardsVisible: {
    type: Number,
    required: true,
  },
  showMoreCardsCount: {
    type: Number,
    default: 0,
  },
  moreCards: {
    type: Array,
    default: () => [],
  },
});

const store = useStore();

const { iconPath, getCustomIconStyle } = useCustomIcon();
const { translate } = useTranslate();
const { inEpicBoard } = useBoardEmbed();
const { isGuestReader } = useCurrentRole();

const displayShowMorePopup = ref(false);
const documentBody = ref(document.body);
const showMoreButton = ref<HTMLElement | null>(null);
const isHovered = ref(false);

const itemBackgroundStyle = computed(() => {
  return {
    gridArea: `1 / ${props.dayIndex + 1} / span ${props.maxCardsVisible + 3} / span 1`,
  };
});

const itemHeaderStyle = computed(() => {
  return {
    gridArea: `1 / ${props.dayIndex + 1} / span 1 / span 1`,
  };
});

const footerIconStyle = computed(() => {
  return getCustomIconStyle(iconPath('chevron-down'));
});

const monthLabel = computed(() => {
  return format(props.dateTimestamp, 'LLL', { weekStartsOn: 1 });
});

const showWeekend = computed(() => store.getters.getShowWeekends);

// show month label for first day of the month
// or if weekend is disabled, show month label for the first day of the first week of the month
const showMonthLabel = computed(() => {
  const { dateTimestamp, dayIndex } = props;
  return (
    isFirstDayOfMonth(dateTimestamp) ||
    (!showWeekend.value &&
      dayIndex === 0 &&
      isWeekend(startOfMonth(dateTimestamp)) &&
      getWeekOfMonth(dateTimestamp, { weekStartsOn: 1 }) === 2)
  );
});

// isLongMonth is boolean, true if the index is 0 and it is first week of the month
// const isLongMonth = computed(() => {
//   return false;
// });

const monthClass = computed(() => {
  return 'item-short-month';
});

const calendarDayItemClass = computed(() => {
  return {
    today: props.isToday,
    'hover-active': isHovered.value,
  };
});

const dayLabel = computed(() => {
  return getDate(props.dateTimestamp);
});

const quickCard = computed(() => store.getters.getQuickCard);

const getDatesForQuickCard = () => {
  const start_date = millisecondsToSeconds(props.dateTimestamp);
  const due_date = millisecondsToSeconds(getTime(endOfDay(props.dateTimestamp)));
  return { start_date, due_date };
};

const currentTeamId = computed(() => store.getters.getTeamID);

const moreCardsButtonLabel = computed(() => {
  return displayShowMorePopup.value ? translate('hideAllCards') : translate('showAllCards');
});

const handleQuickCardButtonClick = () => {
  const quickCardDraftKey = getQuickCardCreationDraftKey(currentTeamId.value);
  const existingQuickCard = browserStorageGetItem(quickCardDraftKey);
  const newQuickCardObject = existingQuickCard
    ? { ...existingQuickCard, ...getDatesForQuickCard() }
    : { ...quickCard.value, ...getDatesForQuickCard() };

  if (!existingQuickCard) {
    store.dispatch('setQuickCardObject', { quickCardObject: newQuickCardObject });
  } else {
    browserStorageSetItem(quickCardDraftKey, newQuickCardObject);
  }

  store.dispatch('setQuickCard', true);
};

const handleMouseOver = () => {
  if (isHovered.value) return;
  isHovered.value = true;
};

const handleMouseLeave = () => {
  if (!isHovered.value) return;
  isHovered.value = false;
};

const getShowMorePopupStyle = () => {
  if (!showMoreButton.value) return;

  const width = `${showMoreButton.value?.$el.clientWidth}px`;
  return {
    maxWidth: width,
    width,
  };
};

const onDropdownStateChange = (state: any) => {
  displayShowMorePopup.value = state.isVisible;
};
</script>

<style lang="scss">
$distanceFromContainer: 7px;
.calendar-day-item {
  @apply contents;

  .calendar-day-item-background {
    @apply relative;
  }

  .calendar-day-item-footer {
    padding: $calendarCardRowGap $distanceFromContainer $distanceFromContainer;
    width: 100%;
    z-index: 1;
    @apply absolute bottom-0 box-border bg-background-strongest;

    &:hover {
      background-color: inherit;
    }

    .calendar-day-item-footer-button {
      @apply flex items-center justify-between gap-1 px-2 py-1 rounded cursor-pointer;

      &.show-more-active,
      &:hover {
        @apply bg-icon-bg;
      }

      .calendar-day-footer-label {
        @apply text-sm text-text-default truncate;
      }

      .calendar-day-footer-icon {
        @apply bg-icon-default shrink-0;
      }
    }
  }

  &.hover-active {
    .calendar-day-item-background {
      @apply bg-hover-default;

      .calendar-day-item-footer {
        background-color: inherit;
      }
    }
  }

  .calendar-day-item-header {
    @apply flex items-center justify-between relative;
    line-height: $calendarDayHeaderLineHeight;
    padding: $calendarDayHeaderVerticalPadding $calendarDayHeaderHorizontalPadding
      $calendarDayHeaderBottomPadding;

    .item-date-wrap {
      @apply flex items-center justify-between gap-1 overflow-hidden cursor-default;

      .item-day,
      .item-short-month {
        @apply font-medium;
      }

      .item-short-month {
        @apply text-sm text-inactive-text truncate;
      }

      .item-day {
        font-size: 14px;
        @apply text-text-default;
      }
    }

    &.item-past {
      .item-date-wrap {
        .item-day {
          @apply text-inactive-text;
        }
      }
    }

    .add-card-toolbar-icon {
      @apply text-sm text-text-default w-auto bg-transparent border-none cursor-pointer rounded-sm py-[2px];

      .custom-icons {
        @apply w-4 h-4;
      }

      &:hover {
        @apply bg-icon-bg;
      }
    }
  }

  &.today {
    .calendar-day-item-header {
      .item-date-wrap {
        .item-day,
        .item-short-month {
          @apply text-gray-800;
        }
      }
    }
    .item-date-wrap {
      // used static colors because it should look the same in all themes
      @apply py-[2px] px-2 bg-warning-300 rounded;
    }
  }
}
</style>
